import React from 'react';

const SelectContext = React.createContext({});

export const Select = ({ children, value, onValueChange, defaultValue }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState(value || defaultValue || '');

  React.useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleValueChange = (newValue) => {
    setSelectedValue(newValue);
    onValueChange?.(newValue);
    setIsOpen(false);
  };

  return (
    <SelectContext.Provider value={{ isOpen, setIsOpen, selectedValue, handleValueChange }}>
      <div className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger = React.forwardRef(({ className = '', children, ...props }, ref) => {
  const { isOpen, setIsOpen, selectedValue } = React.useContext(SelectContext);
  
  return (
    <button
      ref={ref}
      type="button"
      role="combobox"
      aria-expanded={isOpen}
      className={`
        flex h-10 w-full items-center justify-between rounded-md border
        border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
        disabled:cursor-not-allowed disabled:opacity-50 ${className}
      `}
      onClick={() => setIsOpen(!isOpen)}
      {...props}
    >
      {children}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4 opacity-50"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

export const SelectValue = React.forwardRef(({ placeholder, className = '', ...props }, ref) => {
  const { selectedValue } = React.useContext(SelectContext);
  
  return (
    <span ref={ref} {...props}>
      {selectedValue || placeholder || 'Select...'}
    </span>
  );
});
SelectValue.displayName = 'SelectValue';

export const SelectContent = React.forwardRef(({ className = '', children, ...props }, ref) => {
  const { isOpen } = React.useContext(SelectContext);
  
  if (!isOpen) return null;
  
  return (
    <div
      ref={ref}
      className={`
        absolute z-50 min-w-[8rem] overflow-hidden rounded-md border
        border-gray-200 bg-white text-gray-950 shadow-md animate-in
        fade-in-80 w-full mt-1 ${className}
      `}
      {...props}
    >
      <div className="p-1">
        {children}
      </div>
    </div>
  );
});
SelectContent.displayName = 'SelectContent';

export const SelectItem = React.forwardRef(({ className = '', children, value, ...props }, ref) => {
  const { handleValueChange, selectedValue } = React.useContext(SelectContext);
  const isSelected = selectedValue === value;
  
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={isSelected}
      className={`
        relative flex w-full cursor-pointer select-none items-center
        rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none
        hover:bg-gray-100 focus:bg-gray-100
        data-[disabled]:pointer-events-none data-[disabled]:opacity-50
        ${isSelected ? 'bg-gray-100' : ''}
        ${className}
      `}
      onClick={() => handleValueChange(value)}
      {...props}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </span>
      )}
      {children || value}
    </div>
  );
});
SelectItem.displayName = 'SelectItem';